import {
  Box,
  Button,
  Typography,
  Container,
  Stack,
  Paper,
  useTheme,
  alpha,
} from "@mui/material";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import HomeIcon from "@mui/icons-material/Home";
import DashboardIcon from "@mui/icons-material/Dashboard";

const NotFoundPage = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, 
        ${alpha(theme.palette.primary.main, 0.05)} 0%, 
        ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
      }}
    >
      <Container maxWidth="md">
        {/* Main Content */}
        <Box
          sx={{
            minHeight: "calc(100vh - 80px)", // Adjust based on header height
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            py: 8,
          }}
        >
          {/* Drone Icon with Animation */}
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: "50%",
              mb: 4,
              background: `linear-gradient(135deg, 
                ${theme.palette.primary.main} 0%, 
                ${theme.palette.primary.dark} 100%)`,
              animation: "float 3s ease-in-out infinite",
              "@keyframes float": {
                "0%, 100%": { transform: "translateY(0px)" },
                "50%": { transform: "translateY(-10px)" },
              },
            }}
          >
            <FlightTakeoffIcon
              sx={{
                fontSize: 64,
                color: "white",
                transform: "rotate(-15deg)",
              }}
            />
          </Paper>

          {/* Error Message */}
          <Typography
            variant="h2"
            fontWeight={700}
            sx={{
              mb: 2,
              background: `linear-gradient(135deg, 
                ${theme.palette.primary.main} 0%, 
                ${theme.palette.primary.dark} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: { xs: "2.5rem", md: "3.5rem" },
            }}
          >
            404
          </Typography>

          <Typography
            variant="h4"
            fontWeight={600}
            color="text.primary"
            sx={{ mb: 2, fontSize: { xs: "1.5rem", md: "2rem" } }}
          >
            Flight Path Not Found
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 5,
              maxWidth: 600,
              fontSize: "1.1rem",
              lineHeight: 1.6,
            }}
          >
            Looks like our drone lost its GPS signal while mapping this page.
            The navigation system is recalibrating. Let's guide you back to
            familiar airspace.
          </Typography>

          {/* Action Buttons */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Button
              href="/dashboard"
              variant="contained"
              size="large"
              startIcon={<DashboardIcon />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                background: `linear-gradient(135deg, 
                  ${theme.palette.primary.main} 0%, 
                  ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 4px 20px ${alpha(
                  theme.palette.primary.main,
                  0.3
                )}`,
                "&:hover": {
                  boxShadow: `0 6px 25px ${alpha(
                    theme.palette.primary.main,
                    0.4
                  )}`,
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Return to Dashboard
            </Button>

            <Button
              href="/"
              variant="outlined"
              size="large"
              startIcon={<HomeIcon />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                "&:hover": {
                  borderWidth: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Back to Home Base
            </Button>
          </Stack>

          {/* Additional Info */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 6,
              opacity: 0.7,
              fontSize: "0.9rem",
            }}
          >
            Flight Control Code: 404 | Mission Status: Redirecting
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default NotFoundPage;
